import { GroupFilters } from './GroupFilters';
import { PhaseFilters } from './PhaseFilters';
import { useClientTerminology } from '@/views/list/NetworkTerms';
import { Network } from './Network';
import {
  useFetchFieldsQuery,
  useFetchNetworkFieldDataQuery,
  usePhasingTypes,
  useVoltageInKv,
} from '@/features/fieldSlice';

import { VoltageFilter } from './VoltageFilter';
import { FilterSection } from './FilterSection';
import type { UseFormRegister, Control } from 'react-hook-form';
import { type FilterState } from '@/features/filterSlice';
import { useAppSelector } from '@/app/hooks';

interface NetworkFiltersProps {
  register: UseFormRegister<FilterState>;
  control: Control<FilterState>;
  showTitle?: boolean;
  showNetworkSelector?: boolean;
  showGroups?: boolean;
  showPhase?: boolean;
  showVoltage?: boolean;
}

export const NetworkFilters = ({
  register,
  control,
  showTitle = true,
  showNetworkSelector = true,
  showGroups = true,
  showPhase = true,
  showVoltage = true,
}: NetworkFiltersProps) => {
  const intl = useClientTerminology();
  const { activeGroupName } = useAppSelector((s) => s.analysisMap);

  const { data: fields } = useFetchFieldsQuery();
  const availableFields = fields?.map((i) => i.field);
  const { data: networks } = useFetchNetworkFieldDataQuery(undefined, {
    skip: !availableFields || !availableFields.includes('network'),
  });

  const hasMultipleNetworks = Object.keys(networks ?? {}).length > 1;
  const phasingTypes = usePhasingTypes(fields);
  const voltageOptions = useVoltageInKv(fields);

  return (
    <FilterSection
      title={
        showTitle
          ? intl.formatMessage({ id: 'filter.networkSection' })
          : undefined
      }
    >
      {showNetworkSelector && networks && hasMultipleNetworks && (
        <Network networks={networks} register={register} />
      )}
      {showGroups && (
        <GroupFilters control={control} disabled={!!activeGroupName} />
      )}
      {showPhase && (
        <PhaseFilters phasingTypes={phasingTypes} control={control} />
      )}
      {showVoltage && voltageOptions && voltageOptions.length > 0 && (
        <VoltageFilter control={control} options={voltageOptions} />
      )}
    </FilterSection>
  );
};
